/* eslint-disable no-console */
import { useState, useCallback, useRef, useEffect } from 'react'

import debounce from 'lodash.debounce'

import axios from 'axios'

// Fetches query-scoped suggestions for the AI-mode search dropdown, split by product type.
// Response shape: { data: { cg: [[phrase, { thumbnailUrl, itemTitle, total }]], print_ready: [...] } }.
// Returns null until a non-empty query resolves, so the dropdown falls back to its curated columns.
export default function useGroupedLiveSearch({ query = '', enabled = false } = {}) {
  const [results, setResults] = useState(null)
  const currentRequest = useRef(null)
  const requestCounter = useRef(0)

  const fetchGrouped = useCallback(
    debounce(async (value) => {
      requestCounter.current += 1
      const currentRequestId = requestCounter.current

      if (currentRequest.current) {
        currentRequest.current.cancel()
      }

      const cancelToken = axios.CancelToken.source()
      currentRequest.current = cancelToken

      try {
        const response = await axios.get('/api/internal/items/grouped_live_search', {
          params: { query: value },
          headers: { 'Cache-Control': 'no-cache' },
          cancelToken: cancelToken.token,
          timeout: 3000,
        })

        if (currentRequestId !== requestCounter.current) return

        setResults(response.data?.data || null)
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching grouped suggestions:', error)
        }
      }
    }, 300),
    []
  )

  useEffect(() => {
    const trimmed = query.trim()

    if (!enabled || !trimmed) {
      fetchGrouped.cancel()
      setResults(null)
      return undefined
    }

    fetchGrouped(trimmed)

    return () => fetchGrouped.cancel()
  }, [query, enabled, fetchGrouped])

  useEffect(() => {
    return () => {
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }
    }
  }, [])

  return results
}
